import * as z from 'zod';

export const GetSubscriptionOptionRequest = z.object({
  _id: z
    .string()
    .describe('Subscription option ID.')
    .min(1)
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetSubscriptionOptionResponse = z.object({
  _id: z
    .string()
    .describe(
      'Subscription option ID (auto-generated upon subscription option creation).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  title: z
    .string()
    .describe('Subscription option title.')
    .min(1)
    .max(20)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe('Subscription option description (optional).')
    .max(60)
    .optional()
    .nullable(),
  subscriptionSettings: z
    .object({
      frequency: z
        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
        .describe('Frequency of recurring payment.')
        .optional(),
      autoRenewal: z
        .boolean()
        .describe(
          'Whether subscription is renewed automatically at the end of each period.'
        )
        .optional(),
      billingCycles: z
        .number()
        .int()
        .describe(
          'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
        )
        .min(2)
        .max(999)
        .optional()
        .nullable(),
    })
    .describe(
      'Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
    )
    .optional(),
  discount: z
    .object({
      type: z
        .enum(['UNDEFINED', 'AMOUNT', 'PERCENT'])
        .describe('Discount type.')
        .optional(),
      value: z.number().describe('Discount value.').min(0).optional(),
    })
    .describe(
      'Discount info (optional).\nFor example, a $20 discount would be `value: 20`, `type: AMOUNT`.'
    )
    .optional(),
});
export const GetSubscriptionOptionsForProductRequest = z.object({
  productId: z.string().describe('Product ID.').min(1),
  options: z
    .object({
      includeHiddenSubscriptionOptions: z
        .boolean()
        .describe(
          'Whether to include hidden subscription options in the results.'
        )
        .optional(),
    })
    .describe('Options.')
    .optional(),
});
export const GetSubscriptionOptionsForProductResponse = z.object({
  subscriptionOptions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Subscription option ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        hidden: z
          .boolean()
          .describe(
            'Whether the subscription option is hidden for the product (the default is false).'
          )
          .optional(),
        title: z
          .string()
          .describe('Subscription option title.')
          .min(1)
          .max(20)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Subscription option description (optional).')
          .max(60)
          .optional()
          .nullable(),
        subscriptionSettings: z
          .object({
            frequency: z
              .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
              .describe('Frequency of recurring payment.')
              .optional(),
            autoRenewal: z
              .boolean()
              .describe(
                'Whether subscription is renewed automatically at the end of each period.'
              )
              .optional(),
            billingCycles: z
              .number()
              .int()
              .describe(
                'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
              )
              .min(2)
              .max(999)
              .optional()
              .nullable(),
          })
          .describe(
            'Subscription payment settings. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
          )
          .optional(),
        discount: z
          .object({
            type: z
              .enum(['UNDEFINED', 'AMOUNT', 'PERCENT'])
              .describe('Discount type.')
              .optional(),
            value: z.number().describe('Discount value.').min(0).optional(),
          })
          .describe('Discount info (optional).')
          .optional(),
      })
    )
    .optional(),
});
export const GetProductIdsForSubscriptionOptionRequest = z.object({
  _id: z
    .string()
    .describe('Subscription option ID.')
    .min(1)
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      includeHiddenProducts: z
        .boolean()
        .describe('Whether to include hidden products in the returned results.')
        .optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Amount of items to load per page.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe(
              'Number of items to skip in the display (relevant for all pages after the first).'
            )
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Optional pagination parameters')
        .optional(),
    })
    .describe('Paging and other options.')
    .optional(),
});
export const GetProductIdsForSubscriptionOptionResponse = z.object({
  productIds: z.array(z.string()).optional(),
  metadata: z
    .object({
      items: z
        .number()
        .int()
        .describe('Amount of items to load per page.')
        .optional(),
      offset: z
        .number()
        .int()
        .describe(
          'Number of items to skip in the display (relevant for all pages after the first).'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
  totalResults: z
    .number()
    .int()
    .describe('Number of total results.')
    .optional(),
});
export const GetOneTimePurchasesStatusRequest = z.object({
  productId: z.string().describe('Product ID.').min(1),
});
export const GetOneTimePurchasesStatusResponse = z.object({
  allowed: z
    .boolean()
    .describe(
      'Whether the specified product is available for one-time purchase'
    )
    .optional(),
});
export const CreateSubscriptionOptionRequest = z.object({
  subscriptionOption: z
    .object({
      _id: z
        .string()
        .describe(
          'Subscription option ID (auto-generated upon subscription option creation).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      title: z.string().describe('Subscription option title.').min(1).max(20),
      description: z
        .string()
        .describe('Subscription option description (optional).')
        .max(60)
        .optional()
        .nullable(),
      subscriptionSettings: z
        .object({
          frequency: z
            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
            .optional(),
          autoRenewal: z
            .boolean()
            .describe(
              'Whether subscription is renewed automatically at the end of each period.'
            )
            .optional(),
          billingCycles: z
            .number()
            .int()
            .describe(
              'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
            )
            .min(2)
            .max(999)
            .optional()
            .nullable(),
        })
        .describe(
          'Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
        ),
      discount: z
        .object({
          type: z.enum(['UNDEFINED', 'AMOUNT', 'PERCENT']).optional(),
          value: z.number().describe('Discount value.').min(0).optional(),
        })
        .describe(
          'Discount info (optional).\nFor example, a $20 discount would be `value: 20`, `type: AMOUNT`.'
        )
        .optional(),
    })
    .describe('Subscription option info.'),
});
export const CreateSubscriptionOptionResponse = z.object({
  _id: z
    .string()
    .describe(
      'Subscription option ID (auto-generated upon subscription option creation).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  title: z
    .string()
    .describe('Subscription option title.')
    .min(1)
    .max(20)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe('Subscription option description (optional).')
    .max(60)
    .optional()
    .nullable(),
  subscriptionSettings: z
    .object({
      frequency: z
        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
        .describe('Frequency of recurring payment.')
        .optional(),
      autoRenewal: z
        .boolean()
        .describe(
          'Whether subscription is renewed automatically at the end of each period.'
        )
        .optional(),
      billingCycles: z
        .number()
        .int()
        .describe(
          'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
        )
        .min(2)
        .max(999)
        .optional()
        .nullable(),
    })
    .describe(
      'Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
    )
    .optional(),
  discount: z
    .object({
      type: z
        .enum(['UNDEFINED', 'AMOUNT', 'PERCENT'])
        .describe('Discount type.')
        .optional(),
      value: z.number().describe('Discount value.').min(0).optional(),
    })
    .describe(
      'Discount info (optional).\nFor example, a $20 discount would be `value: 20`, `type: AMOUNT`.'
    )
    .optional(),
});
export const UpdateSubscriptionOptionRequest = z.object({
  _id: z
    .string()
    .describe(
      'Subscription option ID (auto-generated upon subscription option creation).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  subscriptionOption: z
    .object({
      _id: z
        .string()
        .describe(
          'Subscription option ID (auto-generated upon subscription option creation).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      title: z
        .string()
        .describe('Subscription option title.')
        .min(1)
        .max(20)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe('Subscription option description (optional).')
        .max(60)
        .optional()
        .nullable(),
      subscriptionSettings: z
        .object({
          frequency: z
            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
            .optional(),
          autoRenewal: z
            .boolean()
            .describe(
              'Whether subscription is renewed automatically at the end of each period.'
            )
            .optional(),
          billingCycles: z
            .number()
            .int()
            .describe(
              'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
            )
            .min(2)
            .max(999)
            .optional()
            .nullable(),
        })
        .describe(
          'Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
        )
        .optional(),
      discount: z
        .object({
          type: z.enum(['UNDEFINED', 'AMOUNT', 'PERCENT']).optional(),
          value: z.number().describe('Discount value.').min(0).optional(),
        })
        .describe(
          'Discount info (optional).\nFor example, a $20 discount would be `value: 20`, `type: AMOUNT`.'
        )
        .optional(),
    })
    .describe('Subscription option update options.'),
});
export const UpdateSubscriptionOptionResponse = z.object({
  _id: z
    .string()
    .describe(
      'Subscription option ID (auto-generated upon subscription option creation).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  title: z
    .string()
    .describe('Subscription option title.')
    .min(1)
    .max(20)
    .optional()
    .nullable(),
  description: z
    .string()
    .describe('Subscription option description (optional).')
    .max(60)
    .optional()
    .nullable(),
  subscriptionSettings: z
    .object({
      frequency: z
        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
        .describe('Frequency of recurring payment.')
        .optional(),
      autoRenewal: z
        .boolean()
        .describe(
          'Whether subscription is renewed automatically at the end of each period.'
        )
        .optional(),
      billingCycles: z
        .number()
        .int()
        .describe(
          'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
        )
        .min(2)
        .max(999)
        .optional()
        .nullable(),
    })
    .describe(
      'Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
    )
    .optional(),
  discount: z
    .object({
      type: z
        .enum(['UNDEFINED', 'AMOUNT', 'PERCENT'])
        .describe('Discount type.')
        .optional(),
      value: z.number().describe('Discount value.').min(0).optional(),
    })
    .describe(
      'Discount info (optional).\nFor example, a $20 discount would be `value: 20`, `type: AMOUNT`.'
    )
    .optional(),
});
export const DeleteSubscriptionOptionRequest = z.object({
  _id: z
    .string()
    .describe('ID of the subscription option to delete.')
    .min(1)
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteSubscriptionOptionResponse = z.object({});
export const BulkCreateSubscriptionOptionsRequest = z.object({
  subscriptionOptions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Subscription option ID (auto-generated upon subscription option creation).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        title: z.string().describe('Subscription option title.').min(1).max(20),
        description: z
          .string()
          .describe('Subscription option description (optional).')
          .max(60)
          .optional()
          .nullable(),
        subscriptionSettings: z
          .object({
            frequency: z
              .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
              .optional(),
            autoRenewal: z
              .boolean()
              .describe(
                'Whether subscription is renewed automatically at the end of each period.'
              )
              .optional(),
            billingCycles: z
              .number()
              .int()
              .describe(
                'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
              )
              .min(2)
              .max(999)
              .optional()
              .nullable(),
          })
          .describe(
            'Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
          ),
        discount: z
          .object({
            type: z.enum(['UNDEFINED', 'AMOUNT', 'PERCENT']).optional(),
            value: z.number().describe('Discount value.').min(0).optional(),
          })
          .describe(
            'Discount info (optional).\nFor example, a $20 discount would be `value: 20`, `type: AMOUNT`.'
          )
          .optional(),
      })
    )
    .max(100),
});
export const BulkCreateSubscriptionOptionsResponse = z.object({
  subscriptionOptions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Subscription option ID (auto-generated upon subscription option creation).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        title: z
          .string()
          .describe('Subscription option title.')
          .min(1)
          .max(20)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Subscription option description (optional).')
          .max(60)
          .optional()
          .nullable(),
        subscriptionSettings: z
          .object({
            frequency: z
              .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
              .describe('Frequency of recurring payment.')
              .optional(),
            autoRenewal: z
              .boolean()
              .describe(
                'Whether subscription is renewed automatically at the end of each period.'
              )
              .optional(),
            billingCycles: z
              .number()
              .int()
              .describe(
                'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
              )
              .min(2)
              .max(999)
              .optional()
              .nullable(),
          })
          .describe(
            'Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
          )
          .optional(),
        discount: z
          .object({
            type: z
              .enum(['UNDEFINED', 'AMOUNT', 'PERCENT'])
              .describe('Discount type.')
              .optional(),
            value: z.number().describe('Discount value.').min(0).optional(),
          })
          .describe(
            'Discount info (optional).\nFor example, a $20 discount would be `value: 20`, `type: AMOUNT`.'
          )
          .optional(),
      })
    )
    .optional(),
});
export const BulkUpdateSubscriptionOptionsRequest = z.object({
  subscriptionOptions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Subscription option ID (auto-generated upon subscription option creation).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        title: z
          .string()
          .describe('Subscription option title.')
          .min(1)
          .max(20)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Subscription option description (optional).')
          .max(60)
          .optional()
          .nullable(),
        subscriptionSettings: z
          .object({
            frequency: z
              .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
              .optional(),
            autoRenewal: z
              .boolean()
              .describe(
                'Whether subscription is renewed automatically at the end of each period.'
              )
              .optional(),
            billingCycles: z
              .number()
              .int()
              .describe(
                'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
              )
              .min(2)
              .max(999)
              .optional()
              .nullable(),
          })
          .describe(
            'Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
          )
          .optional(),
        discount: z
          .object({
            type: z.enum(['UNDEFINED', 'AMOUNT', 'PERCENT']).optional(),
            value: z.number().describe('Discount value.').min(0).optional(),
          })
          .describe(
            'Discount info (optional).\nFor example, a $20 discount would be `value: 20`, `type: AMOUNT`.'
          )
          .optional(),
      })
    )
    .max(100),
});
export const BulkUpdateSubscriptionOptionsResponse = z.object({
  subscriptionOptions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Subscription option ID (auto-generated upon subscription option creation).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        title: z
          .string()
          .describe('Subscription option title.')
          .min(1)
          .max(20)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Subscription option description (optional).')
          .max(60)
          .optional()
          .nullable(),
        subscriptionSettings: z
          .object({
            frequency: z
              .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
              .describe('Frequency of recurring payment.')
              .optional(),
            autoRenewal: z
              .boolean()
              .describe(
                'Whether subscription is renewed automatically at the end of each period.'
              )
              .optional(),
            billingCycles: z
              .number()
              .int()
              .describe(
                'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
              )
              .min(2)
              .max(999)
              .optional()
              .nullable(),
          })
          .describe(
            'Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
          )
          .optional(),
        discount: z
          .object({
            type: z
              .enum(['UNDEFINED', 'AMOUNT', 'PERCENT'])
              .describe('Discount type.')
              .optional(),
            value: z.number().describe('Discount value.').min(0).optional(),
          })
          .describe(
            'Discount info (optional).\nFor example, a $20 discount would be `value: 20`, `type: AMOUNT`.'
          )
          .optional(),
      })
    )
    .optional(),
});
export const BulkDeleteSubscriptionOptionsRequest = z.object({
  ids: z.array(z.string()).max(100),
});
export const BulkDeleteSubscriptionOptionsResponse = z.object({});
export const AssignSubscriptionOptionsToProductRequest = z.object({
  productId: z.string().describe('Product ID.').min(1),
  options: z
    .object({
      assignedSubscriptionOptions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Subscription option ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            hidden: z
              .boolean()
              .describe(
                'Whether the subscription option is hidden for the product (the default is false).'
              )
              .optional(),
            title: z
              .string()
              .describe('Subscription option title.')
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe('Subscription option description (optional).')
              .max(60)
              .optional()
              .nullable(),
            subscriptionSettings: z
              .object({
                frequency: z
                  .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                  .optional(),
                autoRenewal: z
                  .boolean()
                  .describe(
                    'Whether subscription is renewed automatically at the end of each period.'
                  )
                  .optional(),
                billingCycles: z
                  .number()
                  .int()
                  .describe(
                    'Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.'
                  )
                  .min(2)
                  .max(999)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Subscription payment settings. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
              )
              .optional(),
            discount: z
              .object({
                type: z.enum(['UNDEFINED', 'AMOUNT', 'PERCENT']).optional(),
                value: z.number().describe('Discount value.').min(0).optional(),
              })
              .describe('Discount info (optional).')
              .optional(),
          })
        )
        .max(6)
        .optional(),
    })
    .describe('Subscription option assignment options.')
    .optional(),
});
export const AssignSubscriptionOptionsToProductResponse = z.object({});
export const AllowOneTimePurchasesRequest = z.object({
  productId: z.string().describe('Product ID.').min(1),
  allowed: z
    .boolean()
    .describe(
      'Pass `true` to offer product by subscription and as one-time purchase. Pass `false` to offer product as subscription only.'
    ),
});
export const AllowOneTimePurchasesResponse = z.object({});
